import React, { createContext, useEffect, useState } from 'react'

import { GenericResponse, GenericStateType } from 'utils/types'

import { api } from '../../utils/api'
import { getError } from '../../utils/helpers/getError'
import { useCustomContext } from '../../utils/helpers/useCustomContext'

import {
  ValidateUseLoginResponseType,
  ValidateUserContextType,
  ValidateUserForwardSmsType,
  ValidateUserParamsType,
  ValidateUserPinType,
  ValidateUserStateType,
} from './validateUserTypes'


const userValidation: ValidateUserParamsType = {
  isLogin: false,
  user: {
    email: '',
    password: '',
    phoneNumber: '',
  },
}

const INITIAL_VALIDATE_USER_STEP_CONTEXT: ValidateUserContextType = {
  setLoading: () => null,
  setError: () => null,
  clearError: () => null,
  nextStep: () => null,
  prevStep: () => null,
  backTo: () => null,
  setStateByParams: () => userValidation,
  forwardSms: () => new Promise(() => null),
  activateUser: () => new Promise(() => null),
  validateUserState: {
    error: '',
    loading: false,
    state: {
      step: 0,
      isLogin: false,
      userValidation: {
        email: '',
        password: '',
        phoneNumber: '',
      },
    },
  },
}

const ValidateUserStepContext = createContext<ValidateUserContextType>(
  INITIAL_VALIDATE_USER_STEP_CONTEXT,
)

const ValidateUserProvider: React.FC = ({ children }) => {
  const [validateUserStepState, setValidateUserStepState] = useState<
    GenericStateType<ValidateUserStateType>
  >(INITIAL_VALIDATE_USER_STEP_CONTEXT.validateUserState)

  const setLoading = (value = true) =>
    setValidateUserStepState(prevState => ({ ...prevState, loading: value }))

  const setError = (msg: string) =>
    setValidateUserStepState(prevState => ({ ...prevState, loading: false, error: msg }))

  const clearError = () =>
    setValidateUserStepState(prevState => ({ ...prevState, error: '' }))

  const nextStep = () =>
    setValidateUserStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        step: ++validateUserStepState.state.step,
      },
    }))

  const prevStep = () =>
    setValidateUserStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        step: --validateUserStepState.state.step,
      },
    }))

  const backTo = (step: number) =>
    setValidateUserStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        step,
      },
    }))

  const setStateByParams = ({ isLogin, user }: ValidateUserParamsType) => {
    setValidateUserStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        isLogin,
        userValidation: user,
      },
    }))
  }

  const forwardSms = async (data: ValidateUserForwardSmsType) => {
    try {
      setLoading()

      await api.post<GenericResponse<ValidateUseLoginResponseType>>('Auth/ManualLogin', data)

      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const activateUser = async (data: ValidateUserPinType) => {
    try {
      setLoading()
      await api.post<GenericResponse<string>>('Auth/ActivateUser', data)

      setLoading(false)

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  useEffect(() => {
    setValidateUserStepState(prevState => ({
      ...prevState,
      state: {
        ...prevState.state,
        step: 1,
      },
    }))
  }, [])

  return (
    <ValidateUserStepContext.Provider
      value={{
        validateUserState: validateUserStepState,
        setLoading,
        setError,
        clearError,
        nextStep,
        prevStep,
        backTo,
        setStateByParams,
        forwardSms,
        activateUser,
      }}>
      {children}
    </ValidateUserStepContext.Provider>
  )
}

const useValidateUserStep = () => useCustomContext(ValidateUserStepContext)

export { useValidateUserStep, ValidateUserProvider }
